"use client";

import dynamic from "next/dynamic";
import { ComponentType, ReactNode } from "react";
import { Skeleton } from "@/app/ui/atomic/animations";

type SkeletonConfig = 
  | { type: "card"; height: number; count?: number }
  | { type: "horizontal-scroll"; itemWidth: number; itemHeight: number; count?: number }
  | { type: "custom"; render: () => ReactNode };

interface LazyComponentOptions {
  skeleton: SkeletonConfig;
}

function renderSkeleton(config: SkeletonConfig): ReactNode {
  switch (config.type) {
    case "card":
      return (
        <div className="theme-card-alt rounded-xl p-6 shadow-md">
          <Skeleton variant="text" width="60%" height={28} className="mx-auto mb-4" />
          <div className="space-y-4">
            <Skeleton variant="rectangular" height={config.height} className="rounded-lg" />
          </div>
        </div>
      );
    
    case "horizontal-scroll":
      const count = config.count || 4;
      return (
        <div className="w-full py-8">
          <div className="flex gap-4 overflow-hidden px-4">
            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className="flex-shrink-0" style={{ width: config.itemWidth }}>
                <Skeleton variant="card" height={config.itemHeight} />
              </div>
            ))}
          </div>
        </div>
      );
    
    case "custom":
      return config.render();
    
    default:
      return <Skeleton variant="rectangular" height={200} />;
  }
}

export function createLazyComponent<P extends object>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  options: LazyComponentOptions
): ComponentType<P> {
  return dynamic(importFn, {
    loading: () => <>{renderSkeleton(options.skeleton)}</>,
  });
}
